import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { 
  Search, 
  Filter, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

interface Node {
  id: string;
  title: string;
  type: 'note' | 'project' | 'document';
  tags: string[];
  connections: number;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Link {
  source: string | Node;
  target: string | Node;
  strength: number;
}

const Graph: React.FC = () => {
  const { colors } = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLabels, setShowLabels] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'note' | 'project' | 'document'>('all');

  const mockNodes: Node[] = [
    { id: '1', title: 'React Fundamentals', type: 'note', tags: ['React', 'JavaScript'], connections: 5 },
    { id: '2', title: 'Component Patterns', type: 'note', tags: ['React', 'Patterns'], connections: 3 },
    { id: '3', title: 'State Management', type: 'note', tags: ['React', 'Redux'], connections: 4 },
    { id: '4', title: 'React Learning Path', type: 'project', tags: ['Learning', 'React'], connections: 8 },
    { id: '5', title: 'JavaScript ES6', type: 'note', tags: ['JavaScript', 'ES6'], connections: 6 },
    { id: '6', title: 'Async Programming', type: 'note', tags: ['JavaScript', 'Async'], connections: 3 },
    { id: '7', title: 'React Documentation', type: 'document', tags: ['React', 'Docs'], connections: 2 },
    { id: '8', title: 'TypeScript Basics', type: 'note', tags: ['TypeScript', 'JavaScript'], connections: 4 },
    { id: '9', title: 'Testing React Apps', type: 'note', tags: ['React', 'Testing'], connections: 3 },
    { id: '10', title: 'Performance Optimization', type: 'note', tags: ['React', 'Performance'], connections: 2 }
  ];

  const mockLinks: Link[] = [
    { source: '1', target: '2', strength: 0.8 },
    { source: '1', target: '3', strength: 0.6 },
    { source: '1', target: '4', strength: 0.9 },
    { source: '2', target: '3', strength: 0.7 },
    { source: '4', target: '5', strength: 0.5 },
    { source: '5', target: '6', strength: 0.8 },
    { source: '1', target: '7', strength: 0.4 },
    { source: '5', target: '8', strength: 0.7 },
    { source: '1', target: '9', strength: 0.6 },
    { source: '2', target: '10', strength: 0.5 }
  ];

  const getNodeColor = (type: string) => {
    switch (type) {
      case 'note': return colors.blue;
      case 'project': return colors.green;
      case 'document': return colors.yellow;
      default: return colors.subtext0;
    }
  };

  const getNodeSize = (connections: number) => {
    return Math.max(8, Math.min(20, connections * 2));
  };

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 800;
    const height = 600;

    const filteredNodes = mockNodes.filter(node => {
      const matchesSearch = node.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || node.type === filterType;
      return matchesSearch && matchesType;
    });

    const filteredLinks = mockLinks.filter(link => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      return filteredNodes.some(n => n.id === sourceId) && filteredNodes.some(n => n.id === targetId);
    });

    const simulation = d3.forceSimulation(filteredNodes)
      .force('link', d3.forceLink(filteredLinks).id((d: any) => d.id).strength(0.1))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30));

    const container = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        container.attr('transform', event.transform);
      });

    svg.call(zoom as any);

    // Links
    const link = container.append('g')
      .selectAll('line')
      .data(filteredLinks)
      .enter().append('line')
      .attr('stroke', colors.surface1)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', (d: any) => Math.sqrt(d.strength * 5));

    // Nodes
    const node = container.append('g')
      .selectAll('circle')
      .data(filteredNodes)
      .enter().append('circle')
      .attr('r', (d: any) => getNodeSize(d.connections))
      .attr('fill', (d: any) => getNodeColor(d.type))
      .attr('stroke', colors.base)
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .call(d3.drag<SVGCircleElement, Node>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }));

    // Labels
    const labels = container.append('g')
      .selectAll('text')
      .data(filteredNodes)
      .enter().append('text')
      .text((d: any) => d.title)
      .attr('font-size', 12)
      .attr('font-family', 'system-ui')
      .attr('fill', colors.text)
      .attr('text-anchor', 'middle')
      .attr('dy', -25)
      .style('pointer-events', 'none')
      .style('opacity', showLabels ? 1 : 0);

    // Node interactions
    node
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('r', getNodeSize(d.connections) * 1.2);
        
        setSelectedNode(d);
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('r', getNodeSize(d.connections));
      })
      .on('click', function(event, d) {
        setSelectedNode(d);
      });

    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      node
        .attr('cx', (d: any) => d.x)
        .attr('cy', (d: any) => d.y);

      labels
        .attr('x', (d: any) => d.x)
        .attr('y', (d: any) => d.y);
    });

    // Reset zoom function
    (window as any).resetZoom = () => {
      svg.transition().duration(750).call(
        zoom.transform as any,
        d3.zoomIdentity
      );
    };

  }, [colors, searchTerm, filterType, showLabels]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
          Graph View
        </h1>
        
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowLabels(!showLabels)}
            className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            {showLabels ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            Labels
          </button>
          
          <button 
            onClick={() => (window as any).resetZoom?.()}
            className="px-3 py-2 rounded-lg border transition-colors hover:opacity-80 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
              style={{ color: colors.subtext0 }}
            />
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
              style={{
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            />
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
            style={{
              backgroundColor: colors.mantle,
              borderColor: colors.surface0,
              color: colors.text
            }}
          >
            <option value="all">All Types</option>
            <option value="note">Notes</option>
            <option value="project">Projects</option>
            <option value="document">Documents</option>
          </select>
        </div>
      </div>

      {/* Graph Container */}
      <div className="flex gap-6">
        <div 
          className="flex-1 rounded-xl border overflow-hidden"
          style={{ 
            backgroundColor: colors.mantle,
            borderColor: colors.surface0
          }}
        >
          <svg
            ref={svgRef}
            width="100%"
            height="600"
            style={{ backgroundColor: colors.base }}
          />
        </div>

        {/* Node Details Panel */}
        {selectedNode && (
          <div 
            className="w-80 p-6 rounded-xl border"
            style={{ 
              backgroundColor: colors.mantle,
              borderColor: colors.surface0
            }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text }}>
              Node Details
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                  Title
                </label>
                <p className="mt-1" style={{ color: colors.text }}>
                  {selectedNode.title}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                  Type
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getNodeColor(selectedNode.type) }}
                  />
                  <span style={{ color: colors.text }}>
                    {selectedNode.type.charAt(0).toUpperCase() + selectedNode.type.slice(1)}
                  </span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                  Connections
                </label>
                <p className="mt-1" style={{ color: colors.text }}>
                  {selectedNode.connections}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium" style={{ color: colors.subtext0 }}>
                  Tags
                </label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedNode.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-1 rounded"
                      style={{ 
                        backgroundColor: colors.surface0,
                        color: colors.text
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <button 
              className="w-full mt-6 py-2 px-4 rounded-lg font-medium text-white transition-all hover:opacity-90"
              style={{ backgroundColor: colors.blue }}
            >
              Open {selectedNode.type}
            </button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div 
        className="p-4 rounded-xl border"
        style={{ 
          backgroundColor: colors.mantle,
          borderColor: colors.surface0
        }}
      >
        <h4 className="font-medium mb-3" style={{ color: colors.text }}>
          Legend
        </h4>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.blue }}
            />
            <span className="text-sm" style={{ color: colors.text }}>Notes</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.green }}
            />
            <span className="text-sm" style={{ color: colors.text }}>Projects</span>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: colors.yellow }}
            />
            <span className="text-sm" style={{ color: colors.text }}>Documents</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Graph;